import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { autoAuctionApiSdk } from '../sdk/autoAuctionClient.js';
import { idOf } from '../sdk/identity.js';
import { isUnderway } from '../sdk/schedule.js';
import { auctionHousesStore } from './auction-houses.store.js';
import { biddingStore } from './bidding.store.js';
import { saleIndexStore } from './sale-index.store.js';
import { likedStore } from './liked.store.js';

/**
 * The dashboard's records — the premier rail, the dealer's sales, and the
 * assets they are tracking.
 *
 * THIS IS THE SEAM, and it used to be a flat module of hardcoded records
 * (`stores/dashboard.catalog.js`) with a note saying that pointing the screen
 * at live data meant replacing the selections at the bottom of the file. This
 * is that replacement. The shapes below are still the ones the design
 * specifies, and nothing in the render reads anything else.
 *
 * What it composes, and why each part comes from where it does:
 *
 *   the catalog     — `items`, through the same faceted search Explore reads.
 *                     Every asset on this screen is a real listing with a real
 *                     `_id`, so tapping one can open its detail page.
 *   the schedule    — `sales`, through the sale index. A sale document is one
 *                     LANE, so the rows here group a sale's lanes back together.
 *   the venue       — the auction house the sale runs at, which is where the
 *                     location comes from; the photograph rides on the sale
 *                     itself, because the house record holds no media.
 *   what is live    — the bidding store, i.e. the event runtime. Current bid,
 *                     bid state and the auction lights are runtime facts and
 *                     are read from the runtime; putting them anywhere else
 *                     would be a second opinion about a number the runtime owns.
 *   what is liked   — the liked store's built-in list.
 *
 * The runtime half was absent for a few commits, while the bidding store was
 * still in the mobile portal and this package could not import it. It is back
 * now that the store has split, and the fields it fills were left null rather
 * than approximated in the meantime — which is why nothing downstream had to
 * change to accept them again.
 *
 * A field nothing can answer is absent rather than invented. The seeded records
 * carried a per-record hero focal point, a fabricated VIN, an MMR and a bid on
 * every car; the ones that have no source now render as missing, which is what
 * the components' `v-if`s were always for.
 */

// How many the Premier rail shows. Four, matching the design.
const PREMIER_LIMIT = 4;

// How many cars to ask the recommendations engine for: a rail's worth.
const SUGGESTED_LIMIT = 8;

// One page of catalog is plenty for a landing screen: the rails take eight
// between them and the collection grid shows what the dealer follows, not the
// whole catalog.
const CATALOG_PAGE_SIZE = 24;

// The Premier line: the buy-now value from which a car is a premier vehicle. The
// Premier rail's showpieces are the two-door cars at or above it, and Explore's Premier
// filter is every car at or above it.
// The catalog has no sort, so the rail asks for the high end of the catalog in
// one page and orders it here. The page is large enough to hold every car above
// the line in the demo catalog.
export const PREMIER_MIN_VALUE = 100000;
const PREMIER_PAGE_SIZE = 100;
const isTwoDoor = (item) => /\b2 door\b/i.test(item?.BODYSTYLE ?? '');

// A sale's agenda heading. The seeded records carried this as a literal
// ('Today', 'Tomorrow', 'Sat 22 Aug') because they had no clock behind them.
// It is derived from the sale's start now.
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const startOfDay = (ms) => {
    const date = new Date(ms);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
};
export const agendaDayFor = (startAt, now = new Date()) => {
    if (!startAt) return 'Today';
    const date = new Date(startAt);
    if (Number.isNaN(date.getTime())) return 'Today';
    const dayDelta = Math.round((startOfDay(date.getTime()) - startOfDay(now.getTime())) / 86400000);
    if (dayDelta <= 0) return 'Today';
    if (dayDelta === 1) return 'Tomorrow';
    return `${WEEKDAY_SHORT[date.getDay()]} ${date.getDate()} ${MONTH_SHORT[date.getMonth()]}`;
};

// "3h 33m" until a sale opens. Null once it has started — the row shows a LIVE
// chip instead, and a countdown to a moment already past reads as broken.
export const countdownTo = (startAt, now = new Date()) => {
    if (!startAt) return null;
    const remainingMs = new Date(startAt).getTime() - now.getTime();
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) return null;
    const totalMinutes = Math.floor(remainingMs / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
};

// The runtime's event type onto the display key the sale row's glyph lookup
// speaks. Without this every row falls back to `simulcast` and a timed sale
// draws a megaphone — the fallback exists so a row is never glyphless, not so
// the three types can be told apart by it.
const SALE_TYPE_BY_EVENT_TYPE = {
    automatedAuction: 'automated',
    simulcastAuction: 'simulcast',
    timedAuction: 'timed'
};

// The card's own first photograph. Sort by POSITION the way Results / Asset
// Detail do — `$slice: 1` on the wire keeps whichever element Mongo stored
// first, which is not always POSITION 0, and a blank URL on that element used
// to empty the Premier rail (H14 filters assets without a hero). Accept
// `URL` / `url` and a bare string entry so a shape drift cannot blank Home.
const firstImageUrl = (item) => {
    const images = Array.isArray(item?.IMAGES) ? item.IMAGES : [];
    if (!images.length) {
        const urls = Array.isArray(item?.imageUrls) ? item.imageUrls : [];
        const fromUrls = urls.find((entry) => typeof entry === 'string' && entry.trim());
        return fromUrls ? fromUrls.trim() : null;
    }
    const sorted = images
        .slice()
        .sort((a, b) => (a?.POSITION ?? 0) - (b?.POSITION ?? 0));
    for (const entry of sorted) {
        if (typeof entry === 'string' && entry.trim()) return entry.trim();
        const url = entry?.URL ?? entry?.url;
        if (typeof url === 'string' && url.trim()) return url.trim();
    }
    return null;
};

/** Catalog page from subsetByQuery — envelope or bare array. */
const documentsOf = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.documents)) return response.documents;
    return [];
};

export const dashboardStore = defineStore('dashboardStore', () => {
    const auctionHouses = auctionHousesStore();
    const bidding = biddingStore();
    const saleIndex = saleIndexStore();
    const saved = likedStore();

    const catalog = ref([]);
    const premierCatalog = ref([]);
    // What the recommendations engine answered: cars like the ones in the dealer's BidStack, as
    // catalog cards, and the makes and models it matched them on.
    const suggestedCatalog = ref([]);
    const suggestedBasedOn = ref({ makes: [], models: [] });
    const loading = ref(false);
    const loaded = ref(false);

    /**
     * Pull everything the screen composes from.
     *
     * Settled rather than raced: this is the screen a dealer lands on, and a
     * runtime outage should cost the live bid figures, not the page. Every
     * source degrades to empty on its own.
     */
    /**
     * Ask the server for the cars that look like the ones in the dealer's BidStack.
     *
     * Its own door, because the answer changes when the BidStack does: a dealer who saves a car
     * is owed a new answer without the whole page reloading. Never throws — a failed ask leaves
     * the last answer, which for a first load is none, and Home then draws something else.
     */
    async function loadSuggested() {
        try {
            const result = await autoAuctionApiSdk.getRecommendations({ limit: SUGGESTED_LIMIT });
            suggestedCatalog.value = documentsOf(result);
            suggestedBasedOn.value = {
                makes: result?.basedOn?.makes ?? [],
                models: result?.basedOn?.models ?? []
            };
        } catch (error) {
            console.warn('Recommendations load failed', error);
        }
    }

    async function load() {
        if (loading.value) return;
        loading.value = true;
        try {
            const outcomes = await Promise.allSettled([
                autoAuctionApiSdk
                    .getAssetsSubsetByQuery({}, { startingIndex: 0, size: CATALOG_PAGE_SIZE })
                    .then((result) => { catalog.value = documentsOf(result); }),
                autoAuctionApiSdk
                    .getAssetsSubsetByQuery({ BUYNOWPRICE: `${PREMIER_MIN_VALUE}-` }, { startingIndex: 0, size: PREMIER_PAGE_SIZE })
                    .then((result) => { premierCatalog.value = documentsOf(result); }),
                loadSuggested(),
                saleIndex.ensureLoaded(),
                auctionHouses.ensureLoaded(),
                saved.loadCategories(),
                bidding.loadLots()
            ]);
            for (const outcome of outcomes) {
                if (outcome.status === 'rejected') {
                    console.warn('Dashboard load partially failed', outcome.reason);
                }
            }
            loaded.value = true;
        } finally {
            loading.value = false;
        }
    }

    // itemId -> the runtime lot for it, when the item is running somewhere. The
    // runtime stores a lot under its catalog item's own `_id`, so this is a
    // plain string keying with no translation.
    const lotByAssetId = computed(() => {
        const map = new Map();
        for (const lot of bidding.lots ?? []) {
            const key = lot.assetId ?? lot.itemId ?? lot.id;
            if (key != null) map.set(String(key), lot);
        }
        return map;
    });

    /**
     * A catalog item as the dashboard's asset shape.
     *
     * The bid figures come from the lot when one exists and are absent
     * otherwise, which is the honest answer for a car nobody has scheduled: it
     * has no current bid, because there is no sale to have bid in. Every one of
     * them renders behind a `v-if` for exactly that reason.
     */

    /** Letter codes for AuctionLights — lot when live, else catalog SALELIGHTS. */
    const lightCodesOf = (item, lot) => {
        if (Array.isArray(lot?.lights) && lot.lights.length) return lot.lights;
        const raw = item?.SALELIGHTS;
        if (!Array.isArray(raw) || !raw.length) return null;
        const codes = raw
            .map((entry) => (typeof entry === 'string' ? entry : entry?.NAME))
            .map((code) => (code != null ? String(code).trim() : ''))
            .filter(Boolean);
        return codes.length ? codes : null;
    };

    const assetFor = (item) => {
        const id = idOf(item);
        if (!id) return null;
        const lot = lotByAssetId.value.get(String(id));
        const scheduled = saleIndex.saleFor(id);
        const house = auctionHouses.houseFor(item.AUCTIONID);
        const image = firstImageUrl(item);
        const lights = lightCodesOf(item, lot);
        return {
            id,
            year: item.YEAR ?? null,
            make: item.MAKE ?? null,
            model: [item.MODEL, item.SERIES].filter(Boolean).join(' ') || null,
            vin: item.VIN ?? null,
            miles: item.ODOMETERREADING ?? null,
            location: house ? [house.city, house.state].filter(Boolean).join(', ') : null,
            // The dealer-facing number is `runNumber`, and it only exists once a
            // lane has actually scheduled the asset.
            runNumber: scheduled?.runNumber != null ? String(scheduled.runNumber + 1).padStart(4, '0') : null,
            lights,
            light: lights?.[0] ?? null,
            currentBid: lot ? bidding.liveCurrent(lot) : null,
            // How the runtime settled the asset — 'won' / 'lost' / 'sold' /
            // 'noSale' / 'skipped' — or null while it has not run yet. Read off
            // the store's lotResult so this says what the Bid Stack says.
            result: lot ? bidding.lotResult(lot) : null,
            // Whether the clerk has this asset up RIGHT NOW. The runtime's own
            // answer, over the delta socket, so a live badge is never a guess
            // read off the schedule (QA H4). False for a car no lane is running.
            live: lot ? bidding.onBlock(lot) : false,
            mmr: lot?.mmr || null,
            // Liked is a fact about the dealer's list rather than the block,
            // so it answers for a car no lane is running.
            bidState: lot
                ? (bidding.effStatus(lot) ?? (saved.isLiked(id) ? 'liked' : 'none'))
                : (saved.isLiked(id) ? 'liked' : 'none'),
            hero: image,
            thumbnail: image
        };
    };

    const assets = computed(() => catalog.value.map(assetFor).filter(Boolean));

    /**
     * The showpieces: the most valuable two-door cars that a sale has
     * scheduled, most valuable first. A car no sale runs is left out, because
     * the hero offers a dealer a car to bid on.
     */
    const showpieces = computed(() => premierCatalog.value
        .filter(isTwoDoor)
        .slice()
        .sort((a, b) => (Number(b.BUYNOWPRICE) || 0) - (Number(a.BUYNOWPRICE) || 0))
        .map(assetFor)
        .filter((asset) => asset && (asset.runNumber != null || asset.currentBid != null)));

    /**
     * The hero's curated rail.
     *
     * The showpieces lead, so Home opens on the sports cars of the day. With
     * none, what is actually on the block leads — a car a clerk has up right
     * now. Then cars a lane has scheduled, then the head of the catalog, so the
     * rail is never empty.
     */
    const premier = computed(() => {
        if (showpieces.value.length > 0) return showpieces.value.slice(0, PREMIER_LIMIT);
        const live = assets.value.filter((asset) => asset.currentBid != null);
        if (live.length > 0) return live.slice(0, PREMIER_LIMIT);
        const scheduled = assets.value.filter((asset) => asset.runNumber != null);
        return (scheduled.length > 0 ? scheduled : assets.value).slice(0, PREMIER_LIMIT);
    });

    /**
     * The cars the recommendations engine matched to the dealer's BidStack, best first. Scored
     * on the server across the whole catalog; empty when nothing matches or nothing is saved.
     */
    const suggested = computed(() => suggestedCatalog.value.map(assetFor).filter(Boolean));

    /** The makes the BidStack says the dealer likes — what Suggested's See all opens Explore on. */
    const suggestedMakes = computed(() => suggestedBasedOn.value.makes ?? []);

    /**
     * The assets the dealer liked — the collection's second tab.
     *
     * Reads the liked store's DISPLAY list rather than its live one. This rail
     * is not the Liked route, so an unlike here empties the heart and leaves
     * the card where it is; the card goes on the next route change, when the
     * dealer is no longer pointing at it.
     */
    const likedAssets = computed(() => {
        // The built-in list, hydrated — each entry already carries the
        // year/make/model the liked store resolved for it.
        const liked = saved.displayLiked?.assets ?? [];
        return liked
            .map((entry) => {
                const id = entry?.id;
                if (!id) return null;
                const fromCatalog = assets.value.find((asset) => asset.id === id);
                if (fromCatalog) return fromCatalog;
                // A liked asset outside the page of catalog this screen pulled
                // still belongs on the list. The liked store's own hydrated card
                // is what it has, so the row renders from that and the fields it
                // cannot answer stay absent.
                const lot = lotByAssetId.value.get(String(id));
                return {
                    id,
                    year: entry.year ?? null,
                    make: entry.make ?? null,
                    model: entry.model ?? null,
                    vin: null,
                    miles: entry.miles ?? null,
                    location: null,
                    runNumber: null,
                    lights: lot?.lights ?? null,
                    light: lot?.lights?.[0] ?? null,
                    currentBid: lot ? bidding.liveCurrent(lot) : null,
                    result: lot ? bidding.lotResult(lot) : null,
                    live: lot ? bidding.onBlock(lot) : false,
                    mmr: lot?.mmr || null,
                    // It is on the dealer's list, which is the whole reason it
                    // is in this collection.
                    bidState: lot ? (bidding.effStatus(lot) ?? 'liked') : 'liked',
                    hero: entry.image ?? null,
                    thumbnail: entry.image ?? null
                };
            })
            .filter(Boolean);
    });

    // The ids the hero's bookmark reflects — the real liked set now, not a
    // page-local Set seeded from a hardcoded id list.
    const likedAssetIds = computed(() => saved.likedAssetIds ?? new Set());

    /**
     * Sales, with a sale's lanes grouped back together.
     *
     * A sale document is one lane. The dashboard's row is the SALE — it names a
     * lane, but it counts the assets and the liked picks across every lane of
     * that sale, which is what the seeded records described and what a dealer
     * means when they say they are following a sale.
     */
    const allSales = computed(() => {
        const bySaleName = new Map();
        for (const sale of saleIndex.sales) {
            const saleName = sale.saleName;
            if (!saleName) continue;
            if (!bySaleName.has(saleName)) bySaleName.set(saleName, []);
            bySaleName.get(saleName).push(sale);
        }

        const liked = likedAssetIds.value;
        return [...bySaleName.entries()].map(([saleName, lanes]) => {
            const assetIds = lanes.flatMap((lane) => lane.runList ?? []);
            const house = auctionHouses.houseFor(lanes[0].auctionHouseCode);
            const startAt = lanes.map((lane) => lane.startAt).filter(Boolean).sort()[0] ?? null;
            // The runtime's answer when it has one, the schedule's otherwise.
            // The runtime's own live set, read straight off the bidding store so
            // Home, search and this screen cannot disagree about one sale.
            const live = bidding.lots?.length ? bidding.liveSaleNames.has(saleName) : isUnderway(startAt);
            return {
                id: idOf(lanes[0]) ?? saleName,
                name: saleName,
                type: SALE_TYPE_BY_EVENT_TYPE[lanes[0].eventType] ?? lanes[0].eventType,
                lane: lanes.map((lane) => lane.laneName).filter(Boolean).join(', ') || null,
                // A digital house has no place, so the sale's own location string stands in.
                location: [house?.city, house?.state].filter(Boolean).join(', ') || (lanes[0].location ?? null),
                venue: lanes.find((lane) => lane.venueImageUrl)?.venueImageUrl ?? null,
                status: live ? 'live' : 'upcoming',
                // The moment the earliest lane opens, kept as the record's own
                // ISO string so a caller can put a real date window over it
                // rather than trusting the position of a row in this list.
                startAt,
                startsIn: live ? null : countdownTo(startAt),
                day: agendaDayFor(startAt),
                assetIds,
                likedCount: assetIds.filter((assetId) => liked.has(String(assetId))).length
            };
        });
    });

    /**
     * The sales the dealer follows — the collection's "My auctions" scope.
     *
     * Following is not a server concept yet, so "follows" reads as "has
     * something liked in", which is the closest true statement available and
     * is at least the dealer's own signal rather than a hardcoded id list.
     */
    const followedSales = computed(() => allSales.value.filter((sale) => sale.likedCount > 0));

    return {
        // The raw page of catalog this screen pulled. Exposed rather than kept
        // private because it IS the store's state — everything below derives
        // from it, and a store whose state is unreachable cannot be seeded by a
        // test or inspected in the devtools.
        catalog,
        suggestedCatalog,
        suggestedBasedOn,
        loading,
        loaded,
        load,
        loadSuggested,
        assets,
        premier,
        suggested,
        suggestedMakes,
        likedAssets,
        likedAssetIds,
        allSales,
        followedSales
    };
});

export default dashboardStore;
